import { CalendarCheck, House, LayoutGrid, LayoutDashboard } from 'lucide-react'
import { NavLink } from 'react-router'

import { link, paths, ownerPaths } from '@/app/router/paths.ts'

interface NavItem {
  readonly to: string
  readonly label: string
  readonly Icon: typeof House
  /** Index routes must match exactly, otherwise "/" stays active everywhere. */
  readonly end: boolean
}

// Built during render: the studio prefix is not known while modules load.
function navItems(isOwner: boolean): readonly NavItem[] {
  return [
    { to: link(paths.home), label: 'Главная', Icon: House, end: true },
    { to: link(paths.services), label: 'Услуги', Icon: LayoutGrid, end: false },
    // As in the reference. «Моя запись» explains the private link; contacts
    // are reached from the home visit card, the header arrow and the footer.
    { to: link(isOwner ? ownerPaths.root : paths.account), label: isOwner ? 'Кабинет' : 'Моя запись', Icon: isOwner ? LayoutDashboard : CalendarCheck, end: true },
  ]
}

/**
 * Bottom navigation. Three destinations, each a real screen with real content —
 * nothing here links to an unbuilt phase.
 *
 * `NavLink` produces normal history entries, so the platform Back button keeps
 * working (MASTER.md §10).
 */
export function BottomNav({ isOwner = false }: { readonly isOwner?: boolean }) {
  return (
    // Floating, frosted, inset from the edges: content scrolls beneath it and
    // stays visible through it. The active item gets a filled pill, so the
    // state is shape as well as colour (MASTER.md §11).
    <nav
      aria-label="Основная навигация"
      className="safe-bottom pointer-events-none sticky bottom-0 z-10 px-4 pb-3"
    >
      <ul className="glass pointer-events-auto mx-auto flex max-w-md gap-1 rounded-full p-1.5 shadow-raised ring-1 ring-foreground/10">
        {navItems(isOwner).map(({ to, label, Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className="flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-full py-1.5 text-xs font-medium text-muted-foreground transition-colors aria-[current=page]:bg-foreground/10 aria-[current=page]:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className="size-5"
                    aria-hidden="true"
                    strokeWidth={isActive ? 2.4 : 2}
                  />
                  {label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
